import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import loginImage from "../assets/Mayo/Login_Background.jpg";
import gistLogo from "../assets/Mayo/Logo_Gist_White.svg";
import eyeIcon from "../assets/Icons/eye.svg";
import { LoginStyle } from "../tenants/mayo/login/LoginForm";

function Header() {
  return(
    <HeaderContainer>
      <GistLogo src={gistLogo} />
    </HeaderContainer>
  );
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const hospitalFromStorage = JSON.parse(localStorage.getItem("hospital") || "null");
  const hospitalName = (searchParams.get("hospital") || hospitalFromStorage?.name || "").toLowerCase();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showRequirements, setShowRequirements] = useState(true);
  const requirementsRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (requirementsRef.current && !requirementsRef.current.contains(event.target)) {
        setShowRequirements(false);
      }
    }
    if (showRequirements) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showRequirements]);

  useEffect(() => {
    if (!token) {
      setErrorMessage("Invalid reset link. Please request a new password reset.");
    }
  }, [token]);

  // Password validation
  const validatePassword = (pwd) => {
    const requirements = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
    };

    // Count how many of the 4 character type requirements are met (uppercase, lowercase, number, special)
    const characterTypeCount = 
      (requirements.uppercase ? 1 : 0) +
      (requirements.lowercase ? 1 : 0) +
      (requirements.number ? 1 : 0) +
      (requirements.special ? 1 : 0);

    const hasThreeOfFour = characterTypeCount >= 3;

    return {
      isValid: requirements.length && hasThreeOfFour,
      requirements,
      characterTypeCount,
    };
  };

  const passwordValidation = password ? validatePassword(password) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!token) {
      setErrorMessage("Invalid reset link. Please request a new password reset.");
      return;
    }

    if (!passwordValidation?.isValid) {
      setErrorMessage("Password does not meet requirements.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setIsLoading(true);

    try {
      await axios.post("/api/staffMembers/reset-password", {
        token,
        password,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate(hospitalName ? `/login/${hospitalName}` : "/login");
      }, 2000);
    } catch (error) {
      console.error("Reset password error:", error.response?.data);
      setErrorMessage(
        error.response?.data?.message ||
        "Failed to reset password. The link may have expired. Please request a new one."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <LoginStyle />
        <Container>
          <Header />
          <FormWrapper>
            <Title>Password Reset Successful</Title>
            <SuccessMessage>
              Your password has been reset successfully. Redirecting to login...
            </SuccessMessage>
          </FormWrapper>
        </Container>
      </>
    );
  }

  return (
    <>
      <LoginStyle />
      <Container>
        <Header />
        <FormWrapper>
          {password && showRequirements && (
            <RequirementsPanel ref={requirementsRef}>
              <PasswordRequirements>
                <RequirementTitle>Password requirements:</RequirementTitle>
                <Requirement valid={passwordValidation?.requirements.length}>
                  {passwordValidation?.requirements.length ? "✓" : "✗"} The password must contain at least eight characters
                </Requirement>
                <Requirement valid={passwordValidation?.characterTypeCount >= 3}>
                  {passwordValidation?.characterTypeCount >= 3 ? "✓" : "✗"} Password must contain 3 of the following:
                </Requirement>
                <NestedRequirements>
                  <Requirement valid={passwordValidation?.requirements.uppercase}>
                    {passwordValidation?.requirements.uppercase ? "✓" : "○"} Upper case letters
                  </Requirement>
                  <Requirement valid={passwordValidation?.requirements.lowercase}>
                    {passwordValidation?.requirements.lowercase ? "✓" : "○"} Lower case letters
                  </Requirement>
                  <Requirement valid={passwordValidation?.requirements.number}>
                    {passwordValidation?.requirements.number ? "✓" : "○"} Numeric digits
                  </Requirement>
                  <Requirement valid={passwordValidation?.requirements.special}>
                    {passwordValidation?.requirements.special ? "✓" : "○"} Non-alphanumeric characters
                  </Requirement>
                </NestedRequirements>
              </PasswordRequirements>
            </RequirementsPanel>
          )}

          <Title>Reset Password</Title>
          <FormContainer onSubmit={handleSubmit}>
            <InputWrapper>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setShowRequirements(true)}
                error={errorMessage && password !== confirmPassword}
                disabled={isLoading || !token}
                required
              />
              <InputIcon
                style={showPassword ? null : { opacity: "30%" }}
                src={eyeIcon}
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? "Hide password" : "Show password"}
              />
            </InputWrapper>

            <InputWrapper>
              <Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={errorMessage && password !== confirmPassword}
                disabled={isLoading || !token}
                required
              />
              <InputIcon
                style={showConfirmPassword ? null : { opacity: "30%" }}
                src={eyeIcon}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                title={showConfirmPassword ? "Hide password" : "Show password"}
              />
            </InputWrapper>

            <ErrorWrapper>
              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </ErrorWrapper>

            <Button type="submit" disabled={isLoading || !token}>
              {isLoading ? "Resetting..." : "Reset"}
            </Button>

            <BackButton type="button" onClick={() => navigate(hospitalName ? `/login/${hospitalName}` : "/login")}>
              Back to Login
            </BackButton>
          </FormContainer>
        </FormWrapper>
      </Container>
    </>
  );
};

export default ResetPassword;

// ========================= STYLES =========================

const Container = styled.div`
  background-image: url("${loginImage}");
  background-repeat: no-repeat;
  background-size: 100% 100%;
  color: #1A1A1A;
  font-family: Abraham;
  width: 100vw;
  min-height: 100vh;
  overflow: auto;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  height: fit-content;
  margin-top: 4.63vh;
  margin-inline: 24px;
  z-index: 2;
`;

const GistLogo = styled.img`
  fill: white;
  height: 7.2vh;
`;

const MayoLogo = styled.img`
  fill: white;
  height: 11.2vh;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  position: fixed;
  left: 190px;
  top: 50%;
  transform: translateY(-50%);
  width: 30.677vw;
  min-width: 420px;
  max-width: 560px;
  max-height: 90vh;

  padding: 7.87vh 2vw 6.48vh 2vw;
  overflow-y: auto;

  border-radius: 3.645vw;
  z-index: 1;

  background-color: #FFFFFF00;
  backdrop-filter: blur(30px) brightness(1.2);
  -webkit-backdrop-filter: blur(30px) brightness(1.2);
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 3rem 2rem;
  }

  @media (max-width: 768px) {
    width: min(90vw, 24rem);
    padding: 2.5rem 1.75rem;
  }
`;

const Title = styled.p`
  font-size: 2.6vw;
  font-weight: 600;
  margin: 0;
  margin-bottom: 0.463vw;
  text-align: center;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 19vw;
  gap: 0.5rem;
  margin-top: 1.25rem;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 0.5rem;

  &:last-of-type {
    margin-bottom: 0.35rem;
  }
`;

const Input = styled.input`
  width: 100%;
  background: white;
  border-radius: 1.51vw;
  opacity: 0.8;
  border: ${(props) => props.error ? "2px solid #F02A4C;" : "0"};
  padding: 1.481vh 1.458vw 1.296vh 1.458vw;
  box-sizing: border-box;
  font-size: 0.9375vw;

  &::placeholder {
    opacity: 50%;
    font-size: 0.9375vw;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InputIcon = styled.img`
  position: absolute;
  height: 27px;
  width: 1.146vw;
  right: 0.6vw;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
`;

const RequirementsPanel = styled.div`
  position: fixed;
  top: 50%;
  right: 1.5rem;
  transform: translateY(-50%);
  width: 16rem;
  transition: opacity 0.2s ease;
  z-index: 10;

  @media (max-width: 1024px) {
    position: static;
    transform: none;
    width: 100%;
    margin-top: 1.5rem;
  }
`;

const PasswordRequirements = styled.div`
  width: 100%;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 1rem;
  padding: 1rem;
  font-size: 0.875rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
`;

const RequirementTitle = styled.p`
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #333;
`;

const Requirement = styled.p`
  margin: 0.25rem 0;
  color: ${(props) => (props.valid ? "#28a745" : "#666")};
  font-size: 0.8125rem;
`;

const NestedRequirements = styled.div`
  margin-left: 1rem;
  margin-top: 0.5rem;
`;

const ErrorWrapper = styled.div`
  min-height: 1.5rem;
  margin: 0.35rem 0;
  width: 100%;
`;

const ErrorMessage = styled.p`
  color: #F02A4C;
  font-size: 0.9375rem;
  font-weight: 600;
  text-align: center;
  margin: 0;
`;

const Button = styled.button`
  width: 8.85vw;
  height: 4.62vh;
  background: #F02A4C;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 29px;
  border: 0;
  font-size: 0.9375vw;
  font-weight: 500;
  color: white;
  cursor: pointer;
  margin-top: 0.15rem;
  margin-bottom: 0.35rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #d0253a;
  }
`;

const BackButton = styled.button`
  width: 8.85vw;
  height: 4.62vh;
  background: transparent;
  border: 2px solid #F02A4C;
  border-radius: 29px;
  font-size: 0.9375vw;
  font-weight: 500;
  color: #F02A4C;
  cursor: pointer;
  margin-top: 0.15rem;

  &:hover {
    background: #F02A4C;
    color: white;
  }
`;

const SuccessMessage = styled.p`
  font-size: 1.2rem;
  font-weight: 500;
  text-align: center;
  margin: 0;
  margin-bottom: 2rem;
  color: #333;
  width: 80%;
  line-height: 1.5;
`;

